import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import cn from 'classnames';

import type { CommunityProfileField, ProfileFieldKind, ProfileFieldValue } from '~/interfaces/community';

import { FieldStatus, fieldStatusId } from './ui/FieldStatus';
import { LabeledTextInput } from './ui/LabeledTextInput';

import { displayInputValue, visibilityHintKey } from '../_utils/onboardingProfileFields';

type ProfileFieldInputProps = {
  field: CommunityProfileField;
  value: ProfileFieldValue;
  error?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  onChange: (value: ProfileFieldValue) => void;
};

const inputTypeForKind = (kind: ProfileFieldKind | undefined): string => {
  if (kind === 'date') return 'date';
  if (kind === 'datetime') return 'datetime-local';
  if (kind === 'integer' || kind === 'number') return 'number';
  return 'text';
};

export const ProfileFieldInput: FC<ProfileFieldInputProps> = ({
  field,
  value,
  error,
  disabled,
  hideLabel = false,
  onChange,
}) => {
  const { t } = useTranslation();
  const kind = field.custom_field?.kind;
  const id = `profile-field-${field.id}`;
  const label = field.asked_as || field.display;
  const inputValue = displayInputValue(field, value);
  const hint = t(visibilityHintKey(field.visibility));
  const describedBy = fieldStatusId({ id, error, hint });
  const legendClassName = cn('text-[13px] font-medium leading-4 tracking-[-0.13px] text-black/70', {
    'sr-only': hideLabel,
  });

  if (kind === 'boolean') {
    return (
      <fieldset className="flex flex-col" disabled={disabled} aria-describedby={describedBy}>
        <legend className={legendClassName}>
          {label}
          {field.required ? <span aria-hidden="true"> *</span> : null}
        </legend>
        <div className="mt-3 flex gap-2" role="group">
          {[true, false].map((option) => {
            const selected = value === option;
            const optionLabel = option ? t('community.profile_fields.yes') : t('community.profile_fields.no');
            const optionId = `${id}-${option}`;
            return (
              <button
                key={String(option)}
                id={optionId}
                type="button"
                aria-pressed={selected}
                disabled={disabled}
                className={
                  selected
                    ? 'flex h-11 flex-1 cursor-pointer items-center justify-center rounded-lg border border-community-primary bg-community-primary/10 text-[14px] font-medium text-black'
                    : 'flex h-11 flex-1 cursor-pointer items-center justify-center rounded-lg border border-black/10 bg-white text-[14px] font-medium text-black/70'
                }
                onClick={() => onChange(selected ? null : option)}
              >
                {optionLabel}
              </button>
            );
          })}
        </div>
        <FieldStatus id={id} error={error} hint={hint} />
      </fieldset>
    );
  }

  if (kind === 'list') {
    const selected = Array.isArray(value) ? value : [];
    const options = field.custom_field?.options ?? [];
    return (
      <fieldset className="flex flex-col" disabled={disabled} aria-describedby={describedBy}>
        <legend className={legendClassName}>
          {label}
          {field.required ? <span aria-hidden="true"> *</span> : null}
        </legend>
        <div className="mt-3 flex flex-col gap-2">
          {options.map((option) => {
            const checked = selected.includes(option.value);
            const optionId = `${id}-${option.id}`;
            return (
              <label key={option.id} htmlFor={optionId} className="flex items-center gap-3 text-[14px] text-black">
                <input
                  id={optionId}
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  className="size-4 rounded border-black/20 text-community-primary focus:ring-community-primary/20"
                  onChange={() => {
                    onChange(
                      checked ? selected.filter((entry) => entry !== option.value) : [...selected, option.value],
                    );
                  }}
                />
                {option.value}
              </label>
            );
          })}
        </div>
        <FieldStatus id={id} error={error} hint={hint} />
      </fieldset>
    );
  }

  const inputType = inputTypeForKind(kind);

  return (
    <LabeledTextInput
      id={id}
      name={field.id}
      type={inputType}
      label={label}
      hideLabel={hideLabel}
      required={field.required}
      value={inputValue}
      error={error}
      hint={hint}
      disabled={disabled}
      step={kind === 'integer' ? 1 : undefined}
      inputMode={kind === 'integer' ? 'numeric' : kind === 'number' ? 'decimal' : undefined}
      onChange={(event) => onChange(event.target.value)}
    />
  );
};
